/**
 * Scenario store — OutpostForge frontend
 * Saved what-if scenarios. Primary backend: the module's API (DynamoDB via
 * POST /scenarios). Development fallback: localStorage, used when the API
 * reports scenarios are not configured (503) or is unreachable.
 */

import { ApiError } from './http';
import {
  newId,
  parseSandboxState,
  type KeyValueStorage,
  type SandboxState,
  type Scenario,
} from './sandbox';

const SCENARIOS_KEY = 'outpostforge.scenarios';

export interface ScenarioStore {
  list(): Promise<Scenario[]>;
  save(name: string, state: SandboxState): Promise<Scenario>;
  remove(id: string): Promise<void>;
}

// --- localStorage implementation (dev fallback) ---

export class LocalScenarioStore implements ScenarioStore {
  constructor(private readonly storage: KeyValueStorage = globalThis.localStorage) {}

  private read(): Scenario[] {
    const raw = this.storage.getItem(SCENARIOS_KEY);
    if (!raw) return [];
    try {
      const data = JSON.parse(raw) as Scenario[];
      if (!Array.isArray(data)) return [];
      return data.filter((s) => parseSandboxState(s.state).ok);
    } catch {
      return [];
    }
  }

  async list(): Promise<Scenario[]> {
    return this.read();
  }

  async save(name: string, state: SandboxState): Promise<Scenario> {
    const scenario: Scenario = {
      id: newId('scenario'),
      name,
      createdAt: new Date().toISOString(),
      // Deep copy so later sandbox edits do not mutate the saved scenario
      state: JSON.parse(JSON.stringify(state)) as SandboxState,
    };
    const scenarios = [...this.read(), scenario];
    this.storage.setItem(SCENARIOS_KEY, JSON.stringify(scenarios));
    return scenario;
  }

  async remove(id: string): Promise<void> {
    const scenarios = this.read().filter((scenario) => scenario.id !== id);
    this.storage.setItem(SCENARIOS_KEY, JSON.stringify(scenarios));
  }
}

// --- API implementation (DynamoDB behind the module's backend) ---

import { scenarioService } from './api/scenarioService';

export class ApiScenarioStore implements ScenarioStore {
  async list(): Promise<Scenario[]> {
    const response = await scenarioService.getScenarios();
    return response.scenarios;
  }

  async save(name: string, state: SandboxState): Promise<Scenario> {
    return scenarioService.postScenario({ name, state });
  }

  async remove(id: string): Promise<void> {
    await scenarioService.deleteScenario(id);
  }
}

// --- Resolution with fallback ---

let resolved: Promise<ScenarioStore> | undefined;

/**
 * Use the API store when the backend has scenarios configured; fall back to
 * localStorage in dev (backend answers 503 ScenariosNotConfigured) or when
 * the backend is unreachable.
 */
export function resolveScenarioStore(): Promise<ScenarioStore> {
  if (!resolved) {
    resolved = (async () => {
      try {
        await scenarioService.getScenarios();
        return new ApiScenarioStore();
      } catch (err) {
        if (err instanceof ApiError && err.status !== 503) {
          // API reachable but errored differently — still prefer it
          return new ApiScenarioStore();
        }
        return new LocalScenarioStore();
      }
    })();
  }
  return resolved;
}

/** Reset memoized resolution (tests). */
export function resetScenarioStore(): void {
  resolved = undefined;
}
